import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  runTransaction,
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'grn';

// Auto-generate GRN number: GRN-001, GRN-002, ...
export const generateGRNNumber = async () => {
  try {
    const counterRef = doc(db, 'counters', 'grnNumber');
    const result = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const next = counterDoc.exists() ? (counterDoc.data().current || 0) + 1 : 1;
      transaction.set(counterRef, { current: next }, { merge: true });
      return `GRN-${String(next).padStart(3, '0')}`;
    });
    return { success: true, grnNumber: result };
  } catch (error) {
    // Fallback
    return { success: true, grnNumber: `GRN-${Date.now()}` };
  }
};

// Get all GRNs
export const getGRNs = async () => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return {
      success: true,
      data: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get single GRN
export const getGRNById = async (id) => {
  try {
    const snap = await getDoc(doc(db, COLLECTION, id));
    if (!snap.exists()) return { success: false, error: 'GRN not found' };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create GRN
export const addGRN = async (grnData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...grnData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update GRN
export const updateGRN = async (id, data) => {
  try {
    await updateDoc(doc(db, COLLECTION, id), {
      ...data,
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete GRN — only allowed if no transfers have been made
export const deleteGRN = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTION, id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update available store quantities after a transfer
export const deductGRNStoreQty = async (grnId, transferredItems) => {
  try {
    const grnRef = doc(db, COLLECTION, grnId);
    const snap = await getDoc(grnRef);
    if (!snap.exists()) return { success: false, error: 'GRN not found' };

    const grnData = snap.data();
    const updatedItems = grnData.items.map((item) => {
      const transferred = transferredItems.find((t) => t.itemIndex === item.itemIndex);
      if (!transferred) return item;
      return {
        ...item,
        storeQty: Math.max(0, (item.storeQty ?? item.qtyReceived) - transferred.qty),
        transferredQty: (item.transferredQty || 0) + transferred.qty,
      };
    });

    const allDone = updatedItems.every((i) => (i.storeQty ?? 0) === 0);
    const anyDone = updatedItems.some((i) => (i.transferredQty || 0) > 0);

    await updateDoc(grnRef, {
      items: updatedItems,
      status: allDone ? 'Completed' : anyDone ? 'Partial' : 'Received',
      updatedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};